import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';

import { StackedDialog, useSelectMenuProps } from '../../../utilities/ModalStackContext';
import { StateManager } from '../../../utilities/stateManager';
import { createFinanceDeposit } from '../../../utilities/financeInvoice';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

// Finance fees are almost always paid out of the deal rather than billed to the
// customer separately, so this is deliberately a confirm-and-go dialog: the
// remaining balance is pre-filled and one click books both sides of the transfer.
export default function TransferFromDealModal({
  open,
  onClose,
  stockNumber,
  defaultAmount = 0,
  defaultAccount = '',
  onCreated,
}) {
  const selectMenuProps = useSelectMenuProps();
  const [amount, setAmount] = React.useState('');
  const [account, setAccount] = React.useState(defaultAccount);
  const [memo, setMemo] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setAmount(defaultAmount ? String(defaultAmount) : '');
    setAccount(defaultAccount);
    setMemo('');
  }, [open, defaultAmount, defaultAccount]);

  const handleSave = async () => {
    const value = parseFloat(amount);
    if (!value) {
      StateManager.setAlertAndOpen('Enter an amount to transfer', 'warning');
      return;
    }
    setSaving(true);
    try {
      const deposit = await createFinanceDeposit({
        stockNumber,
        amount: value,
        memo,
        account,
        transferFromDeal: true,
      });
      StateManager.setAlertAndOpen(`${USD(value)} transferred from the deal`, 'success');
      onCreated?.(deposit);
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to record the transfer', 'error');
    }
    setSaving(false);
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Transfer from Deal</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{stockNumber}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Pre-filled with the remaining invoice balance"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Receiving Bank</InputLabel>
            <Select value={account} onChange={(e) => setAccount(e.target.value)} label="Receiving Bank" MenuProps={selectMenuProps}>
              {StateManager.deposit_banks.map(bank => (
                <MenuItem key={bank.value} value={bank.value}>{bank.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Note (optional)"
            size="small"
            fullWidth
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            helperText="Appended to the TFD memo"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Records a finance deposit tagged <strong>TFD</strong> and an offsetting paid
            expense on {stockNumber} from Skyway Classics, so the cost moves onto the
            sales side of the deal.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Record Transfer'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
